import { chartSeriesColors, chartValueAt, chartValueExtents, collectChartCategories, normalizeChartData, } from "./chartLayer.js";
const MARGIN = { left: 0.12, right: 0.04, top: 0.08, bottom: 0.16 };
function plotBox() {
    return {
        x: MARGIN.left,
        y: MARGIN.top,
        w: 1 - MARGIN.left - MARGIN.right,
        h: 1 - MARGIN.top - MARGIN.bottom,
    };
}
function yToPlot(y, min, max, plot) {
    const t = max === min ? 0.5 : (y - min) / (max - min);
    return plot.y + plot.h * (1 - t);
}
function addAxes(plan, categories, min, max, theme) {
    const plot = plotBox();
    const axisColor = theme.palette.muted;
    plan.lines.push({
        x1: plot.x,
        y1: plot.y + plot.h,
        x2: plot.x + plot.w,
        y2: plot.y + plot.h,
        stroke: axisColor,
        width: 1,
    });
    plan.lines.push({
        x1: plot.x,
        y1: plot.y,
        x2: plot.x,
        y2: plot.y + plot.h,
        stroke: axisColor,
        width: 1,
    });
    categories.forEach((cat, i) => {
        const x = plot.x + ((i + 0.5) / Math.max(categories.length, 1)) * plot.w;
        plan.labels.push({
            x,
            y: plot.y + plot.h + 0.05,
            text: cat,
            fill: theme.palette.text,
            anchor: "middle",
            size: 0.035,
        });
    });
    const ticks = 4;
    for (let i = 0; i <= ticks; i += 1) {
        const value = min + ((max - min) * i) / ticks;
        const y = yToPlot(value, min, max, plot);
        plan.labels.push({
            x: plot.x - 0.02,
            y,
            text: String(Math.round(value * 10) / 10),
            fill: theme.palette.muted,
            anchor: "end",
            size: 0.03,
        });
    }
}
function buildBarPlan(data, theme) {
    const normalized = normalizeChartData(data);
    const plan = { rects: [], lines: [], paths: [], polygons: [], labels: [] };
    const categories = collectChartCategories(normalized);
    const { min, max } = chartValueExtents(normalized);
    const plot = plotBox();
    const colors = chartSeriesColors(theme, normalized.series.length);
    const groupW = plot.w / Math.max(categories.length, 1);
    const barW = groupW / Math.max(normalized.series.length, 1) * 0.75;
    addAxes(plan, categories, min, max, theme);
    categories.forEach((cat, ci) => {
        normalized.series.forEach((series, si) => {
            const value = chartValueAt(series, cat);
            const x = plot.x + ci * groupW + si * (groupW / normalized.series.length) + (groupW / normalized.series.length - barW) / 2;
            const yTop = yToPlot(value, min, max, plot);
            const yBase = yToPlot(0, min, max, plot);
            plan.rects.push({
                x,
                y: Math.min(yTop, yBase),
                w: barW,
                h: Math.abs(yBase - yTop),
                fill: colors[si],
            });
        });
    });
    return plan;
}
function buildLinePlan(data, theme) {
    const normalized = normalizeChartData(data);
    const plan = { rects: [], lines: [], paths: [], polygons: [], labels: [] };
    const categories = collectChartCategories(normalized);
    const { min, max } = chartValueExtents(normalized);
    const plot = plotBox();
    const colors = chartSeriesColors(theme, normalized.series.length);
    addAxes(plan, categories, min, max, theme);
    normalized.series.forEach((series, si) => {
        const points = categories.map((cat, ci) => {
            const value = chartValueAt(series, cat);
            const x = plot.x + ((ci + 0.5) / Math.max(categories.length, 1)) * plot.w;
            const y = yToPlot(value, min, max, plot);
            return { x, y };
        });
        for (let i = 1; i < points.length; i += 1) {
            const a = points[i - 1];
            const b = points[i];
            plan.lines.push({
                x1: a.x,
                y1: a.y,
                x2: b.x,
                y2: b.y,
                stroke: colors[si],
                width: 2,
            });
        }
        points.forEach((p) => {
            plan.rects.push({
                x: p.x - 0.008,
                y: p.y - 0.008,
                w: 0.016,
                h: 0.016,
                fill: colors[si],
            });
        });
    });
    return plan;
}
function buildPiePlan(data, theme) {
    const normalized = normalizeChartData(data);
    const plan = { rects: [], lines: [], paths: [], polygons: [], labels: [] };
    const series = normalized.series[0];
    if (!series)
        return plan;
    const total = series.points.reduce((sum, p) => sum + Math.max(0, p.y), 0) || 1;
    const cx = 0.5;
    const cy = 0.46;
    const r = 0.28;
    const colors = chartSeriesColors(theme, series.points.length);
    let angle = -Math.PI / 2;
    series.points.forEach((point, i) => {
        const slice = (Math.max(0, point.y) / total) * Math.PI * 2;
        const start = angle;
        angle += slice;
        const end = angle;
        const steps = Math.max(3, Math.ceil(slice * 24));
        const poly = [{ x: cx, y: cy }];
        for (let s = 0; s <= steps; s += 1) {
            const t = start + (slice * s) / steps;
            poly.push({ x: cx + r * Math.cos(t), y: cy + r * Math.sin(t) });
        }
        plan.polygons.push({ points: poly, fill: colors[i] });
        const mid = start + slice / 2;
        plan.labels.push({
            x: cx + (r + 0.06) * Math.cos(mid),
            y: cy + (r + 0.06) * Math.sin(mid),
            text: point.x,
            fill: theme.palette.text,
            anchor: "middle",
            size: 0.032,
        });
    });
    return plan;
}
export function buildChartRenderPlan(data, theme) {
    const normalized = normalizeChartData(data);
    if (normalized.type === "line")
        return buildLinePlan(normalized, theme);
    if (normalized.type === "pie")
        return buildPiePlan(normalized, theme);
    return buildBarPlan(normalized, theme);
}
